import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Radio,
  Button,
  DatePicker,
  Input,
  message,
  Alert,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Divider,
  Breadcrumb
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  TagOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import EmployeeSelect from '../components/common/EmployeeSelect';
import AttachmentManager from '../components/sop/AttachmentManager';
import MessagePreview from '../components/sop/MessagePreview';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';
import TagSelect from '../components/common/TagSelect';
import massSendService from '../services/massSendService';
import api from '../services/api';
import useMediaState from '../hooks/useMediaState';

const { Title, Text } = Typography;
 

const CustomerMassSend = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  

  // 监听表单变化用于实时预览
  const [formValues, setFormValues] = useState({});

  // 直接管理attachments状态，模仿StepModal的做法
  const [attachments, setAttachments] = useState([]);

  // 判断页面模式：view, copy, edit, create
  const getPageMode = () => {
    const path = location.pathname;
    if (path.includes('/view/')) return 'view';
    if (path.includes('/edit/')) return 'edit';
    if (location.state?.copyFrom) return 'copy';
    return 'create';
  };

  const pageMode = getPageMode();
  const isViewMode = pageMode === 'view';
  const isCopyMode = pageMode === 'copy';
  const isEditMode = pageMode === 'edit';

  // 使用媒体状态管理Hook
  const {
    mediaState,
    setMediaState,
    updateMediaId,
    updateSpecificMediaId,
    updateOssUrl,
    updateImageGroup,
    clearMedia,
    removeFromImageGroup
  } = useMediaState();

  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 不再本地加载标签，交由通用组件内部处理

  // 加载任务数据（查看和编辑模式）
  useEffect(() => {
    const loadTaskData = async () => {
      if (taskId && (isViewMode || isEditMode)) {
        try {
          setLoading(true);
          const response = await massSendService.getTaskById(taskId);
          if (response.success && response.data) {
            const taskData = response.data;
            fillFormWithTaskData(taskData);
          } else {
            message.error('任务不存在');
            navigate('/customer-mass-send');
          }
        } catch (error) {
          console.error('加载任务数据失败:', error);
          message.error('加载任务数据失败');
          navigate('/customer-mass-send');
        } finally {
          setLoading(false);
        }
      }
    };

    loadTaskData();
  }, [taskId, isViewMode, isEditMode, navigate]);

  // 处理复制模式的数据填充
  useEffect(() => {
    if (isCopyMode && location.state?.copyFrom) {
      const taskData = location.state.copyFrom;
      console.log('🔄 复制模式 - 接收到的任务数据:', taskData);
      fillFormWithTaskData(taskData);
    }
  }, [isCopyMode, location.state]);

  // 处理 AI 生成内容自动填入
  useEffect(() => {
    if (location.state?.aiGeneratedText && !isCopyMode && !isViewMode && !isEditMode) {
      const text = location.state.aiGeneratedText;
      const images = location.state.aiGeneratedImages || [];
      const newAttachments = [{ id: `text-${Date.now()}`, type: 'TEXT', content: text }];

      if (images.length > 0) {
        const ossUrls = {};
        images.forEach((img, i) => {
          ossUrls[i] = img.ossUrl || img.url;
        });
        newAttachments.push({
          id: `img-${Date.now()}`,
          type: 'IMAGE_GROUP',
          imageGroup: { mediaIds: [], ossUrls },
        });
      }

      setAttachments(newAttachments);
      message.info('已从 AI 生成内容填入');
    }
  }, [location.state]);

  // 填充表单数据
  const fillFormWithTaskData = (taskData) => {
    try {
      console.log('📋 开始填充表单数据:', taskData);
      console.log('🔍 任务数据所有字段:');
      Object.keys(taskData).forEach(key => {
        console.log(`  ${key}:`, taskData[key], `(${typeof taskData[key]})`);
      });
      // 解析附件数据
      let parsedAttachments = [];
      if (taskData.attachments) {
        try {
          const attachmentData = typeof taskData.attachments === 'string'
            ? JSON.parse(taskData.attachments)
            : taskData.attachments;

          if (Array.isArray(attachmentData)) {
            parsedAttachments = attachmentData;
          }
        } catch (e) {
          console.error('解析附件数据失败:', e);
        }
      }

      // 如果有文本内容但没有TEXT类型附件，添加一个
      if (taskData.textContent && !parsedAttachments.find(att => att.type === 'TEXT')) {
        parsedAttachments.unshift({
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: taskData.textContent
        });
      }

      // 如果没有任何附件，添加默认的TEXT附件
      if (parsedAttachments.length === 0) {
        parsedAttachments = [{
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: taskData.textContent || ''
        }];
      }

      setAttachments(parsedAttachments);

      // 解析目标配置
      let targetConfigObj = null;
      if (taskData.targetConfig) {
        try {
          targetConfigObj = typeof taskData.targetConfig === 'string'
            ? JSON.parse(taskData.targetConfig)
            : taskData.targetConfig;
        } catch (e) {
          console.error('解析目标配置失败:', e);
        }
      }

      // 填充表单
      const formData = {
        taskName: taskData.taskName,
        members: (() => {
          // 尝试多种可能的成员字段名
          const memberData = taskData.members || taskData.memberList || taskData.employees || taskData.users || taskData.executors;
          console.log('🔍 尝试提取成员数据 - memberData:', memberData);

          if (!memberData || !Array.isArray(memberData)) {
            return [];
          }

          return memberData.map(m => {
            // 优先使用 userId，然后是 id，最后是 memberId，并转换为字符串
            const memberId = m.userId || m.id || m.memberId || m.employeeId || m.weworkUserId;
            return memberId ? String(memberId) : null;
          }).filter(Boolean);
        })(),
        targetType: targetConfigObj?.targetType || 'ALL',
        targetTags: targetConfigObj?.tagIds || [],
        excludeTags: targetConfigObj?.excludeTagIds || [],
        scheduleType: taskData.sendTimeType || 'IMMEDIATE',
        scheduleDateTime: taskData.scheduledTime ? dayjs(taskData.scheduledTime) : null
      };

      console.log('📝 准备填充的表单数据:', formData);
      console.log('📎 解析后的附件数据:', parsedAttachments);
      console.log('🎯 目标配置对象:', targetConfigObj);
      console.log('⏰ 原始时间数据:', taskData.scheduledTime);
      console.log('⏰ 解析后时间数据:', formData.scheduleDateTime);
      // 检查所有可能的成员字段
      console.log('👥 检查成员相关字段:');
      console.log('  - taskData.members:', taskData.members);
      console.log('  - taskData.memberList:', taskData.memberList);
      console.log('  - taskData.employees:', taskData.employees);
      console.log('  - taskData.users:', taskData.users);
      console.log('  - taskData.executors:', taskData.executors);

      const memberData = taskData.members || taskData.memberList || taskData.employees || taskData.users || taskData.executors;
      console.log('👥 选中的成员数据源:', memberData);
      console.log('👥 成员数据详细信息:');
      if (memberData && Array.isArray(memberData)) {
        memberData.forEach((member, index) => {
          console.log(`  成员${index + 1}:`, member);
          console.log(`    - userId: ${member.userId} (${typeof member.userId})`);
          console.log(`    - memberId: ${member.memberId} (${typeof member.memberId})`);
          console.log(`    - id: ${member.id} (${typeof member.id})`);
          console.log(`    - employeeId: ${member.employeeId} (${typeof member.employeeId})`);
          console.log(`    - weworkUserId: ${member.weworkUserId} (${typeof member.weworkUserId})`);
          const selectedId = member.userId || member.id || member.memberId || member.employeeId || member.weworkUserId;
          console.log(`    - 选择的ID: ${selectedId} (${typeof selectedId}) -> ${String(selectedId)}`);
        });
      }
      console.log('🎯 提取的成员ID列表:', formData.members);
      console.log('🎯 成员ID类型检查:', formData.members.map(id => `${id}(${typeof id})`));

      form.setFieldsValue(formData);
      setFormValues(formData);
    } catch (error) {
      console.error('填充表单数据失败:', error);
      message.error('填充表单数据失败');
    }
  };

  // 监听表单值变化
  const handleValuesChange = (changedValues, allValues) => {
    // 同步表单值到状态
    setFormValues(allValues);
  };

  // 初始化默认附件
  const initializeAttachments = () => {
    return []; // 返回空数组，让AttachmentManager内部的useEffect自动添加默认的TEXT附件
  };

  // 初始化attachments状态
  useEffect(() => {
    if (attachments.length === 0) {
      // 复制模式下不要用默认内容覆盖从列表页带过来的内容
      if (location.state?.copyFrom) {
        return;
      }
      const defaultAttachments = [
        {
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: ''
        }
      ];
      setAttachments(defaultAttachments);
    }
  }, []);

  // 文件上传处理
  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError, onProgress, fieldKey, mediaType, stepType } = options;

    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

      // 获取租户ID
      const getTenantId = () => {
        try {
          const tenantStr = localStorage.getItem('tenant');
          if (tenantStr) {
            const tenant = JSON.parse(tenantStr);
            return tenant.id;
          }
        } catch (error) {
          console.error('获取租户ID失败:', error);
        }
        return null;
      };

      const tenantId = getTenantId();
      if (!tenantId) {
        throw new Error('无法获取租户信息');
      }

      // 根据文件类型确定mediaType
      let actualMediaType = 'image'; // 默认为图片
      if (file.type.startsWith('video/')) {
        actualMediaType = 'video';
      } else if (file.type.startsWith('image/')) {
        actualMediaType = 'image';
      } else {
        actualMediaType = 'file';
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', actualMediaType);
      formData.append('tenantId', tenantId);
      formData.append('sceneType', 'message'); // 群发消息使用message场景

      const response = await api.post('/media/wework/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
          if (onProgress) onProgress({ percent: progress });
        }
      });

      if (response.data.success) {
        const { mediaId, ossUrl } = response.data;

        // 更新预览URL
        setPreviewUrls(prev => ({ ...prev, [mediaId]: ossUrl }));

        // 调用成功回调
        if (onSuccess) {
          onSuccess({ mediaId, ossUrl, success: true });
        }
      } else {
        throw new Error(response.data.message || '上传失败');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      if (onError) onError(error);
      message.error('文件上传失败');
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  // 文件删除处理
  const handleRemoveFile = (fieldKey, stepType, mediaIdToRemove = null) => {
    // 处理图片组的特殊删除逻辑
    if (mediaIdToRemove) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[mediaIdToRemove];
        return newUrls;
      });
    } else {
      // 普通文件删除
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[fieldKey];
        return newUrls;
      });
    }
  };

  // URL验证函数
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // 获取表单初始值
  const getInitialValues = () => {
    return {
      targetType: 'ALL',
      scheduleType: 'IMMEDIATE'
    };
  };

  // 提取文本内容
  const extractTextContent = (attachments) => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    return textAttachment ? textAttachment.content || '' : '';
  };

  // 提取非文本附件
  const extractNonTextAttachments = (attachments) => {
    return attachments.filter(att => att.type !== 'TEXT');
  };

  // 提交任务
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // 手动验证 attachments
      if (!attachments || attachments.length === 0) {
        message.error('请添加发送内容');
        return;
      }

      const values = await form.validateFields();

      // 提取文本内容和非文本附件
      const textContent = extractTextContent(attachments);
      const nonTextAttachments = extractNonTextAttachments(attachments);

      // 构建任务数据
      const taskData = {
        taskName: values.taskName,
        taskType: 'CUSTOMER',
        sendTimeType: values.scheduleType,
        scheduledTime: values.scheduleType === 'SCHEDULED' ? values.scheduleDateTime?.format('YYYY-MM-DD HH:mm:ss') : null,
        textContent: textContent,
        attachments: nonTextAttachments.length > 0 ? JSON.stringify(nonTextAttachments) : null,
        targetType: values.targetType,
        targetConfig: JSON.stringify({
          targetType: values.targetType,
          tagIds: values.targetType === 'TAG' ? values.targetTags : null,
          excludeTagIds: values.excludeTags || null
        }),
        members: values.members.map(memberId => ({ userId: memberId }))
      };

      // 创建任务
      const result = await massSendService.createTask(taskData);

      if (values.scheduleType === 'IMMEDIATE') {
        // 立即执行
        await massSendService.executeTask(result.data.id);
        message.success('客户群发任务已提交并开始执行');
      } else {
        message.success('客户群发任务已创建，将在指定时间执行');
      }

      // 跳转回列表页
      navigate('/customer-mass-send');

    } catch (error) {
      console.error('提交任务失败:', error);
      message.error(error.response?.data?.message || '提交任务失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setFormValues({});

    // 重置attachments状态
    const defaultAttachments = [
      {
        id: `text-${Date.now()}`,
        type: 'TEXT',
        content: ''
      }
    ];
    setAttachments(defaultAttachments);
  };

  // 获取页面标题
  const getPageTitle = () => {
    switch (pageMode) {
      case 'view': return '查看客户群发';
      case 'copy': return '复制客户群发';
      default: return '创建客户群发';
    }
  };

  // 返回列表页
  const handleGoBack = () => {
    navigate('/customer-mass-send');
  };

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item onClick={() => navigate('/customer-mass-send')}>
          <Title level={5} style={{ cursor: 'pointer', color: '#1677ff' }}>客户群发</Title>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Title level={5}>{getPageTitle()}</Title>
        </Breadcrumb.Item>
      </Breadcrumb>
      {isViewMode && (
        <Tag icon={<EyeOutlined />} color="blue">
          只读模式
        </Tag>
      )}

      <Row gutter={24}>
        {/* 左侧配置区域 */}
        <Col span={14}>
          <Card title="群发设置" loading={loading}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleValuesChange}
              initialValues={getInitialValues()}
            >
              {/* 群发名称 */}
              <Form.Item
                name="taskName"
                label="群发名称"
                rules={[{ required: true, message: '请输入群发名称' }]}
              >
                <Input
                  placeholder="请输入群发任务名称"
                  disabled={isViewMode}
                />
              </Form.Item>

              {/* 发送成员 */}
              <Form.Item
                name="members"
                label={<>发送成员</>}
                rules={[{ required: true, message: '请至少选择一个发送成员' }]}
              >
                <EmployeeSelect
                  multiple
                  placeholder="请选择或搜索发送成员（可多选）"
                  disabled={isViewMode}
                />
              </Form.Item>

              {/* 发送对象 */}
              <Form.Item
                name="targetType"
                label={<>发送对象</>}
                rules={[{ required: true, message: '请选择发送对象' }]}
              >
                <Radio.Group disabled={isViewMode}>
                  <Radio value="ALL">全部客户</Radio>
                  <Radio value="TAG">指定标签客户</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 目标标签 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.targetType !== currentValues.targetType
                }
              >
                {({ getFieldValue }) => {
                  const targetType = getFieldValue('targetType');

                  if (targetType === 'TAG') {
                    return (
                      <Form.Item
                        name="targetTags"
                        label="目标标签"
                        rules={[{ required: true, message: '请至少选择一个标签' }]}
                      >
                        <TagSelect
                          mode="multiple"
                          placeholder="请选择标签（可多选）"
                          disabled={isViewMode}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>

              {/* 排除标签 */}
              <Form.Item
                name="excludeTags"
                label="排除标签"
                help="选择需要排除的标签，拥有这些标签的客户将不会收到消息"
              >
                <TagSelect
                  mode="multiple"
                  placeholder="选择要排除的标签（可选）"
                  disabled={isViewMode}
                />
              </Form.Item>

              <Divider />

              {/* 发送内容 */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                    发送内容
                  </label>
                </div>
                <AttachmentManager
                  stepType="MESSAGE"
                  attachments={attachments}
                  mediaState={mediaState}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  isTemplateActive={false}
                  readOnly={isViewMode}
                  validateUrl={validateUrl}
                  handleFileUpload={handleFileUpload}
                  handleRemoveFile={handleRemoveFile}
                  handleUpdatePreviewUrls={(newUrls) => {
                    setPreviewUrls(prev => ({ ...prev, ...newUrls }));
                  }}
                  onAddAttachment={(type) => {
                    const newAttachment = {
                      id: `${type.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                      type: type
                    };

                    // 根据类型初始化不同的字段
                    switch (type) {
                      case 'TEXT':
                        newAttachment.content = '';
                        break;
                      case 'IMAGE_GROUP':
                        newAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                        break;
                      case 'VIDEO':
                        newAttachment.mediaId = '';
                        newAttachment.ossUrl = '';
                        newAttachment.videoTitle = '';
                        newAttachment.videoDescription = '';
                        break;
                      case 'LINK':
                        newAttachment.title = '';
                        newAttachment.description = '';
                        newAttachment.url = '';
                        newAttachment.picUrl = '';
                        break;
                      case 'MINIPROGRAM':
                        newAttachment.title = '';
                        newAttachment.appid = '';
                        newAttachment.pagepath = '';
                        newAttachment.thumbMediaId = '';
                        newAttachment.thumbMediaId_ossUrl = '';
                        break;
                      default:
                        newAttachment.content = '';
                    }

                    const newAttachments = [...attachments, newAttachment];
                    setAttachments(newAttachments);

                    // 返回新附件的ID，供AttachmentManager的素材选择功能使用
                    return newAttachment.id;
                  }}

                  onRemoveAttachment={(attachmentId) => {
                    const newAttachments = attachments.filter(att => att.id !== attachmentId);
                    setAttachments(newAttachments);
                  }}
                  onUpdateContent={(attachmentId, field, value) => {
                    // 使用函数式更新避免状态竞争
                    setAttachments(prevAttachments => {
                      const newAttachments = prevAttachments.map(att =>
                        att.id === attachmentId ? { ...att, [field]: value } : att
                      );

                      // 在状态更新完成后，更新mediaState
                      const currentAttachment = newAttachments.find(att => att.id === attachmentId);
                      const attachmentType = currentAttachment?.type;

                      // 同时更新mediaState以确保AttachmentManager正常工作
                      if (field === 'mediaId' || field === 'videoMediaId' || field === 'fileMediaId') {
                        setMediaState(prev => {
                          const newState = { ...prev };
                          newState.messageMediaIds = {
                            ...prev.messageMediaIds,
                            [attachmentId]: value
                          };

                          if (field === 'mediaId' && attachmentType === 'IMAGE') {
                            newState.messageImageIds = {
                              ...prev.messageImageIds,
                              [attachmentId]: value
                            };
                          } else if (field === 'videoMediaId' || (field === 'mediaId' && attachmentType === 'VIDEO')) {
                            newState.messageVideoIds = {
                              ...prev.messageVideoIds,
                              [attachmentId]: value
                            };
                          } else if (field === 'fileMediaId' || (field === 'mediaId' && attachmentType === 'FILE')) {
                            newState.messageFileIds = {
                              ...prev.messageFileIds,
                              [attachmentId]: value
                            };
                          }

                          return newState;
                        });
                      }

                      // 处理ossUrl字段
                      if (field === 'ossUrl' || field === 'videoMediaId_ossUrl' || field === 'mediaId_ossUrl' || field === 'thumbMediaId_ossUrl') {
                        setMediaState(prev => {
                          return {
                            ...prev,
                            messageOssUrls: {
                              ...prev.messageOssUrls,
                              [attachmentId]: value
                            }
                          };
                        });
                      }

                      // 处理imageGroup字段
                      if (field === 'imageGroup' && attachmentType === 'IMAGE_GROUP') {
                        setMediaState(prev => {
                          return {
                            ...prev,
                            messageImageGroupIds: {
                              ...prev.messageImageGroupIds,
                              [attachmentId]: value
                            }
                          };
                        });
                      }

                      return newAttachments;
                    });
                  }}
                  onUpdateType={(attachmentId, newType) => {
                    const newAttachments = attachments.map(att => {
                      if (att.id === attachmentId) {
                        // 重置附件，保留id和type，清空其他字段
                        const resetAttachment = { id: att.id, type: newType };

                        // 根据新类型初始化字段
                        switch (newType) {
                          case 'TEXT':
                            resetAttachment.content = '';
                            break;
                          case 'IMAGE_GROUP':
                            resetAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                            break;
                          case 'VIDEO':
                            resetAttachment.mediaId = '';
                            resetAttachment.ossUrl = '';
                            resetAttachment.videoTitle = '';
                            resetAttachment.videoDescription = '';
                            break;
                          case 'LINK':
                            resetAttachment.title = '';
                            resetAttachment.description = '';
                            resetAttachment.url = '';
                            resetAttachment.picUrl = '';
                            break;
                          case 'MINIPROGRAM':
                            resetAttachment.title = '';
                            resetAttachment.appid = '';
                            resetAttachment.pagepath = '';
                            resetAttachment.thumbMediaId = '';
                            resetAttachment.thumbMediaId_ossUrl = '';
                            break;
                          default:
                            resetAttachment.content = '';
                        }

                        return resetAttachment;
                      }
                      return att;
                    });

                    setAttachments(newAttachments);
                  }}
                />
              </div>

              <Divider />

              {/* 群发时间 */}
              <Form.Item
                name="scheduleType"
                label={<>群发时间</>}
                rules={[{ required: true, message: '请选择群发时间' }]}
              >
                <Radio.Group disabled={isViewMode}>
                  <Radio value="IMMEDIATE">立即发送</Radio>
                  <Radio value="SCHEDULED">定时发送</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 定时发送时间 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.scheduleType !== currentValues.scheduleType
                }
              >
                {({ getFieldValue }) => {
                  const scheduleType = getFieldValue('scheduleType');

                  if (scheduleType === 'SCHEDULED') {
                    return (
                      <Form.Item
                        name="scheduleDateTime"
                        label="发送时间"
                        rules={[
                          { required: true, message: '请选择发送时间' },
                          {
                            validator: (_, value) => {
                              if (value && value.isBefore(dayjs())) {
                                return Promise.reject(new Error('发送时间不能早于当前时间'));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime={{ format: 'HH:mm' }}
                          format="YYYY-MM-DD HH:mm"
                          placeholder="请选择发送时间"
                          style={{ width: '100%' }}
                          disabled={isViewMode}
                          disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>


            </Form>
          </Card>
        </Col>

        {/* 右侧预览区域 */}
        <Col span={10}>
          <MessagePreview
            step={{
              type: 'MESSAGE',
              actionType: 'SEND_MESSAGE',
              messageList: attachments.map(attachment => {
                if (attachment.type === 'TEXT') {
                  return {
                    type: 'TEXT',
                    params: { content: attachment.content || '' }
                  };
                } else if (attachment.type === 'IMAGE_GROUP') {
                  return {
                    type: 'IMAGE_GROUP',
                    params: {
                      imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                    }
                  };
                } else if (attachment.type === 'VIDEO') {
                  return {
                    type: 'VIDEO',
                    params: {
                      mediaId: attachment.mediaId || '',
                      ossUrl: attachment.ossUrl || ''
                    }
                  };
                } else if (attachment.type === 'LINK') {
                  return {
                    type: 'LINK',
                    params: {
                      title: attachment.title || '',
                      url: attachment.url || '',
                      description: attachment.description || '',
                      picUrl: attachment.picUrl || '',
                      picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                    }
                  };
                } else if (attachment.type === 'MINIPROGRAM') {
                  return {
                    type: 'MINIPROGRAM',
                    params: {
                      title: attachment.title || '',
                      appid: attachment.appid || '',
                      pagepath: attachment.pagepath || '',
                      thumbMediaId: attachment.thumbMediaId || '',
                      thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                    }
                  };
                }
                return null;
              }).filter(Boolean)
            }}
            noTruncate={true}
          />
        </Col>
      </Row>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      {!isViewMode && (
        <div style={fixedFooterStyle}>
          <Space>
            <Button size="middle" onClick={() => navigate('/customer-mass-send')}>
              取消
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitLoading}
              onClick={handleSubmit}
              size="middle"
            >
              {isCopyMode ? '创建副本' : '提交群发'}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default CustomerMassSend; 